import React, {
  ChangeEvent,
  ReactElement, useCallback,
  useEffect,
  useMemo, useRef,
  useState
} from 'react';
import Autocomplete, { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete';
import {ListItem} from '@material-ui/core';
import NxGenericTextField, {fieldClassName} from '../nxGenericTextField/NxGenericTextField';
import clsx from 'clsx';
import {logMissingOnChange} from '../inputError';
import {isEqual, debounce, uniqWith} from 'lodash/fp';

export interface NxAutocompleteDataProviderOutput<T_Value> {
  values: T_Value[],
}

export type NxAutocompleteDataProvider<T_Value> = (text: string) => Promise<NxAutocompleteDataProviderOutput<T_Value>>;

export interface NxAutocompleteProps<T_Value> {
  dataProvider: NxAutocompleteDataProvider<T_Value>;
  labelProvider: (value: T_Value) => string;
  disabled?: boolean;
  label: string;
  name: string;
  onBlur?: () => void;
  onChange?: (value: T_Value | null) => void;
  className?: string;
  value?: T_Value | null | undefined;
  required?: boolean;
  error?: string;
}

export const NxAutocomplete = function<T_Value>(props: NxAutocompleteProps<T_Value>): ReactElement {
  const {
    dataProvider,
    labelProvider,
    disabled,
    label,
    className,
    value,
    onBlur,
    onChange,
    name,
    required,
    error
  } = props;
  const previousValue = useRef(value);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value ? labelProvider(value) : '');
  const [dataProviderOutput, setDataProviderOutput] = useState<NxAutocompleteDataProviderOutput<T_Value>>({
    values: [],
  });

  const fetch: (text: string, cb: (output: NxAutocompleteDataProviderOutput<T_Value>) => void) => void = useMemo(
    () => debounce(400, (request: string, cb: (output: NxAutocompleteDataProviderOutput<T_Value>) => void): void => {
      dataProvider(request).then(cb);
    }),
    [dataProvider]
  );

  const handleChange = useCallback((value: T_Value | null): void => {
    if (!onChange) {
      logMissingOnChange('Autocomplete', name);
      return;
    }

    previousValue.current = value;
    onChange(value);
  }, [name, onChange]);

  const newValue = value !== null ? value : previousValue.current;
  previousValue.current = newValue;

  useEffect(() => {
    let active = true;
    setLoading(true);

    fetch(inputValue, output => {
      if(!active) {
        return;
      }

      let newOptions: T_Value[] = [];

      if (newValue) {
        newOptions = [newValue];
      }

      if (output.values) {
        newOptions = uniqWith(isEqual, [...newOptions, ...output.values]);
      }

      setDataProviderOutput({
        values: newOptions
      });

      setLoading(false);
    });

    return (): void => {
      active = false;
    };
  }, [inputValue, newValue, fetch]);

  // there is a bug in autocomplete component from material-ui, which causes
  // it to display for a brief moment in top left corner of the screen
  // https://github.com/mui-org/material-ui/issues/8423
  return (
    // wrap autocomplete in a div, so that if its parent is a flex container, dropdown will display correctly
    <div className={clsx(className, fieldClassName)}>
      <Autocomplete<T_Value, false, false, false>
        getOptionLabel={(value: T_Value): string => labelProvider(value)}
        options={dataProviderOutput.values}
        autoComplete
        includeInputInList
        disabled={disabled}
        disablePortal={true}
        filterSelectedOptions
        getOptionSelected={(val1, val2): boolean => isEqual(val1, val2)}
        loading={loading}
        onClose={(): void => {
          setLoading(false);
        }}
        value={newValue ?? null}
        onBlur={(): void => {
          if(!onBlur) {
            return;
          }

          onBlur();
        }}
        onInputChange={(event: ChangeEvent<HTMLInputElement>): void => {
          if(event) {
            setInputValue(event.target.value);
          }
        }}
        onChange={(event: ChangeEvent, newValue): void => {
          handleChange(newValue);
        }}
        renderInput={(params: AutocompleteRenderInputParams): ReactElement =>
          <div ref={params.InputProps.ref}>
            <NxGenericTextField
              disabled={disabled}
              inputProps={{...params.inputProps, 'data-test': name}}
              required={required}
              error={error}
              label={label}
              value={inputValue} />
          </div>
        }
        renderOption={(option: T_Value): ReactElement => <ListItem>
          {labelProvider(option)}
        </ListItem>}
      />
    </div>
  );
};

